/**
 * CanvasSessionManager — tracks what agents have created/modified in the
 * current conversation thread. Provides session context string for follow-up
 * requests so agents know what exists on the canvas.
 */
import type { CanvasAction } from '@/services/api/agentApi';

const MAX_HISTORY = 50;
const MAX_SESSIONS = 20;

interface ActionHistoryEntry {
  userMessage: string;
  step: string;
  actions: CanvasAction[];
  timestamp: number;
}

interface PropertyChange {
  componentId: string;
  propertyPath: string;   // e.g., "style.backgroundColor", "properties.content"
  oldValue: unknown;
  newValue: unknown;
  timestamp: number;
  userMessage: string;
}

const MAX_PROPERTY_CHANGES = 200;

interface SessionData {
  createdComponentIds: string[];
  modifiedComponentIds: string[];
  createdPageIds: string[];
  actionHistory: ActionHistoryEntry[];
  propertyChangeLog: PropertyChange[];
  lastDataProfile?: Record<string, unknown>;
  lastAccessedAt: number;
}

const sessions = new Map<string, SessionData>();

function getOrCreate(threadId: string): SessionData {
  if (!sessions.has(threadId)) {
    // Evict oldest session if at capacity
    if (sessions.size >= MAX_SESSIONS) {
      let oldestKey: string | null = null;
      let oldestTime = Infinity;
      for (const [key, data] of sessions.entries()) {
        if (data.lastAccessedAt < oldestTime) {
          oldestTime = data.lastAccessedAt;
          oldestKey = key;
        }
      }
      if (oldestKey) sessions.delete(oldestKey);
    }
    sessions.set(threadId, {
      createdComponentIds: [],
      modifiedComponentIds: [],
      createdPageIds: [],
      actionHistory: [],
      propertyChangeLog: [],
      lastAccessedAt: Date.now(),
    });
  }
  const session = sessions.get(threadId)!;
  session.lastAccessedAt = Date.now();
  return session;
}

/** Extract property-level changes from a changes object and append to session log */
function logPropertyChanges(
  session: SessionData,
  componentId: string,
  changes: Record<string, unknown> | undefined,
  userMessage: string,
): void {
  if (!changes || typeof changes !== 'object') return;

  for (const [key, value] of Object.entries(changes)) {
    // Skip id/componentId keys — they're identifiers, not property changes
    if (key === 'id' || key === 'componentId') continue;

    if (key === 'style' && typeof value === 'object' && value !== null) {
      for (const [styleProp, styleVal] of Object.entries(value as Record<string, unknown>)) {
        session.propertyChangeLog.push({
          componentId,
          propertyPath: `style.${styleProp}`,
          oldValue: undefined,
          newValue: styleVal,
          timestamp: Date.now(),
          userMessage,
        });
      }
    } else if (key === 'properties' && typeof value === 'object' && value !== null) {
      for (const [prop, propVal] of Object.entries(value as Record<string, unknown>)) {
        session.propertyChangeLog.push({
          componentId,
          propertyPath: `properties.${prop}`,
          oldValue: undefined,
          newValue: propVal,
          timestamp: Date.now(),
          userMessage,
        });
      }
    } else {
      session.propertyChangeLog.push({
        componentId,
        propertyPath: key,
        oldValue: undefined,
        newValue: value,
        timestamp: Date.now(),
        userMessage,
      });
    }
  }

  // Cap property change log
  if (session.propertyChangeLog.length > MAX_PROPERTY_CHANGES) {
    session.propertyChangeLog = session.propertyChangeLog.slice(-MAX_PROPERTY_CHANGES);
  }
}

/**
 * Record actions produced by a streaming step.
 */
export function recordActions(
  threadId: string,
  userMessage: string,
  step: string,
  actions: CanvasAction[],
): void {
  const session = getOrCreate(threadId);

  for (const action of actions) {
    if (action.type === 'add_component') {
      const id = action.payload.component?.id as string | undefined;
      if (id && !session.createdComponentIds.includes(id)) {
        session.createdComponentIds.push(id);
      }
    } else if (action.type === 'update_component') {
      const id = action.payload.componentId;
      if (id && !session.modifiedComponentIds.includes(id)) {
        session.modifiedComponentIds.push(id);
      }
      // Record property-level changes
      if (id) {
        logPropertyChanges(session, id, action.payload.changes as Record<string, unknown> | undefined, userMessage);
      }
    } else if (action.type === 'batch_update') {
      // batch_update: track componentId from payload root
      const rootId = action.payload.componentId;
      if (rootId && !session.modifiedComponentIds.includes(rootId)) {
        session.modifiedComponentIds.push(rootId);
      }
      // batch_update: track per-component from payload.components[]
      const components = action.payload.components as Array<Record<string, unknown>> | undefined;
      if (Array.isArray(components)) {
        for (const comp of components) {
          const compId = (comp.id || comp.componentId) as string | undefined;
          if (compId && !session.modifiedComponentIds.includes(compId)) {
            session.modifiedComponentIds.push(compId);
          }
          if (compId) {
            logPropertyChanges(session, compId, comp, userMessage);
          }
        }
      }
    } else if (action.type === 'add_page') {
      const pageId = action.payload.pageId;
      if (pageId && !session.createdPageIds.includes(pageId)) {
        session.createdPageIds.push(pageId);
      }
    }
  }

  session.actionHistory.push({ userMessage, step, actions, timestamp: Date.now() });

  // Trim history
  if (session.actionHistory.length > MAX_HISTORY) {
    session.actionHistory = session.actionHistory.slice(-MAX_HISTORY);
  }
}

/**
 * Store the data profile from a data-first generation.
 */
export function setLastDataProfile(threadId: string, profile: Record<string, unknown>): void {
  const session = getOrCreate(threadId);
  session.lastDataProfile = profile;
}

/**
 * Build a compact session context string for agent requests.
 * Sent as part of the request body so agents know about prior actions.
 */
export function getSessionContext(threadId: string): string {
  const session = sessions.get(threadId);
  if (!session) return '';

  const parts: string[] = [];

  if (session.createdComponentIds.length > 0) {
    parts.push(`Created components: ${session.createdComponentIds.join(', ')}`);
  }

  if (session.modifiedComponentIds.length > 0) {
    parts.push(`Modified components: ${session.modifiedComponentIds.join(', ')}`);
  }

  if (session.createdPageIds.length > 0) {
    parts.push(`Created pages: ${session.createdPageIds.join(', ')}`);
  }

  // Summarize recent actions (last 5 entries)
  const recent = session.actionHistory.slice(-5);
  if (recent.length > 0) {
    const summaries = recent.map(entry => {
      const actionTypes = [...new Set(entry.actions.map(a => a.type))];
      return `"${entry.userMessage.slice(0, 80)}" → ${actionTypes.join(', ')} (${entry.actions.length} actions)`;
    });
    parts.push(`Recent actions:\n${summaries.join('\n')}`);
  }

  // Recent property changes (last 10)
  if (session.propertyChangeLog.length > 0) {
    const recentChanges = session.propertyChangeLog.slice(-10);
    const changeLines = recentChanges.map(
      change => `  - ${change.componentId}: ${change.propertyPath} → ${JSON.stringify(change.newValue)} ("${change.userMessage.slice(0, 60)}")`
    );
    parts.push(`Recent property changes:\n${changeLines.join('\n')}`);
  }

  if (session.lastDataProfile) {
    parts.push(`Last data profile: ${JSON.stringify(session.lastDataProfile).slice(0, 300)}`);
  }

  return parts.join('\n');
}

/**
 * Check whether a thread has any recorded session activity.
 */
export function hasSession(threadId: string): boolean {
  const session = sessions.get(threadId);
  return !!session && (
    session.createdComponentIds.length > 0 ||
    session.modifiedComponentIds.length > 0 ||
    session.actionHistory.length > 0
  );
}

/**
 * Clear session data for a thread.
 */
export function clearSession(threadId: string): void {
  sessions.delete(threadId);
}

/**
 * Get all created component IDs for a thread.
 */
export function getCreatedComponentIds(threadId: string): string[] {
  return sessions.get(threadId)?.createdComponentIds || [];
}

/**
 * Get all modified component IDs for a thread.
 */
export function getModifiedComponentIds(threadId: string): string[] {
  return sessions.get(threadId)?.modifiedComponentIds || [];
}

/**
 * Get all created page IDs for a thread.
 */
export function getCreatedPageIds(threadId: string): string[] {
  return sessions.get(threadId)?.createdPageIds || [];
}
